import React, { useEffect, useMemo, useState } from 'react';
import { formsCatalogueAPI } from './api';
import WorkflowRenderer from './WorkflowRenderer';
import './gpj-modules.css';

const displayStatus = (status) => ({
  active: 'Active official form',
  locked_not_in_force: 'Locked — not yet in force',
  retired: 'Retired'
}[status] || 'Status unavailable');

const safeError = (error, fallback) => (
  error.response?.data?.detail || fallback
);

function FormCard({ form, user, onGuide, onStart, onLogin }) {
  const locked = form.status === 'locked_not_in_force';

  return (
    <article className={`gpj-form-card${locked ? ' gpj-form-card--locked' : ''}`}>
      <div className="gpj-form-no">{form.form_number}</div>
      <div className="gpj-form-body">
        <h3>{form.title}</h3>
        <p className="gpj-what">{form.intent}</p>
        <div className="gpj-form-tags">
          <span className="gpj-tag">{form.category}</span>
          {form.court_scope && (
            <span className="gpj-tag gpj-tag--court">{form.court_scope}</span>
          )}
          <span className={`gpj-tag${locked ? ' gpj-tag--locked' : ''}`}>
            {displayStatus(form.status)}
          </span>
        </div>

        {locked && (
          <p className="gpj-lock-note">
            No field guide or guided help is available unless Ontario Court
            Services confirms this form is in force.
          </p>
        )}

        <div className="gpj-form-actions">
          <a
            className={`gpj-a-link ${locked
              ? 'gpj-a-link--locked'
              : 'gpj-a-link--primary'}`}
            href={form.official_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {locked ? 'View official status' : 'Open official form'}
            <span className="gpj-ext" aria-hidden="true">↗</span>
          </a>
          {form.field_guide_available && (
            <button
              type="button"
              className="gpj-a-link gpj-a-link--secondary"
              onClick={() => onGuide(form)}
            >
              Field guide
            </button>
          )}
          {form.guided_workflow_available && (
            <button
              type="button"
              className="gpj-a-link gpj-a-link--guided"
              onClick={() => (user ? onStart(form) : onLogin())}
            >
              {user ? 'Start / resume guided help' : 'Log in for guided help'}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function FieldGuide({ guide, onWritingReview, user }) {
  const [open, setOpen] = useState(true);

  return (
    <section
      className={`gpj-fieldguide${open ? ' is-open' : ''}`}
      id={`field-guide-${guide.form_id}`}
    >
      <button
        type="button"
        className="gpj-fg-head"
        onClick={() => setOpen((isOpen) => !isOpen)}
        aria-expanded={open}
      >
        <span className="gpj-fg-copy">
          <span className="gpj-fg-title" role="heading" aria-level="3">
            Field guide: {guide.title}
          </span>
          <span className="gpj-fg-sub">
            Version {guide.guide_version} · plain-language approved content
          </span>
        </span>
        <span className="gpj-fg-chev" aria-hidden="true">▼</span>
      </button>

      <div className="gpj-fg-body">
        {guide.sections.map((section) => (
          <div className="gpj-fg-field" key={section.section_id}>
            <div className="gpj-fname">{section.name}</div>
            <div className="gpj-fex">{section.help_text}</div>
          </div>
        ))}

        <div className="gpj-boundary">
          <div className="gpj-bcol gpj-bcol--can">
            <h4>✓ What GPJ assistance may do</h4>
            <ul>{guide.assistant_can.map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
          <div className="gpj-bcol gpj-bcol--cant">
            <h4>✗ What it never does</h4>
            <ul>{guide.assistant_never.map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
        </div>

        <p className="gpj-disclaimer gpj-disclaimer--compact">
          <b>Why the line is drawn here.</b> Good Parents Justice provides
          administrative support, not legal advice. What to put in the form is
          always your decision.
        </p>

        <div className="gpj-form-actions gpj-form-actions--spaced">
          <button
            type="button"
            className="gpj-a-link"
            onClick={() => onWritingReview(user ? 'writing-review' : 'login')}
          >
            {user ? 'Open Writing Review' : 'Log in to use Writing Review'}
          </button>
          <span className="gpj-writing-boundary-note">
            No form text or answers are transferred. Paste only text you choose.
          </span>
        </div>
      </div>
    </section>
  );
}

export default function FormsLibrary({ user = null, setCurrentView = () => {} }) {
  const [catalogue, setCatalogue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [query, setQuery] = useState('');
  const [guide, setGuide] = useState(null);
  const [guideLoading, setGuideLoading] = useState(false);
  const [workflow, setWorkflow] = useState(null);
  const [accessRequired, setAccessRequired] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await formsCatalogueAPI.list();
        if (active) setCatalogue(response.data);
      } catch (requestError) {
        if (active) {
          setError(safeError(requestError, 'Unable to load the forms catalogue.'));
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  const forms = catalogue?.forms || [];
  const filters = useMemo(
    () => ['All', ...new Set(forms.map((form) => form.category))],
    [forms]
  );
  const visible = useMemo(() => forms.filter((form) => {
    const matchesFilter = activeFilter === 'All' || form.category === activeFilter;
    const normalizedQuery = query.trim().toLowerCase();
    const matchesQuery = !normalizedQuery
      || form.form_number.toLowerCase().includes(normalizedQuery)
      || form.title.toLowerCase().includes(normalizedQuery)
      || form.intent.toLowerCase().includes(normalizedQuery);
    return matchesFilter && matchesQuery;
  }), [activeFilter, forms, query]);

  const showGuide = async (form) => {
    setGuideLoading(true);
    setActionError('');
    try {
      const response = await formsCatalogueAPI.getFieldGuide(
        form.form_id,
        form.catalogue_version,
        form.field_guide_version
      );
      setGuide(response.data);
    } catch (requestError) {
      setActionError(safeError(requestError, 'Unable to load this field guide.'));
    } finally {
      setGuideLoading(false);
    }
  };

  const startWorkflow = async (form) => {
    setActionError('');
    setAccessRequired(false);
    try {
      const response = await formsCatalogueAPI.startWorkflow(form.form_id);
      setWorkflow(response.data);
    } catch (requestError) {
      if (requestError.response?.status === 403) {
        setAccessRequired(true);
      } else {
        setActionError(safeError(requestError, 'Unable to start guided help.'));
      }
    }
  };

  if (workflow && user) {
    return (
      <div className="gpj">
        <div className="gpj-wrap gpj-form-workflow-back">
          <button
            type="button"
            className="gpj-a-link"
            onClick={() => setWorkflow(null)}
          >
            ← Back to forms catalogue
          </button>
        </div>
        <WorkflowRenderer
          workflowId={workflow.workflow_id}
          instanceId={workflow.instance_id}
          userId={user.id}
          onNavigate={setCurrentView}
        />
      </div>
    );
  }

  return (
    <div className="gpj">
      <section className="gpj-section">
        <div className="gpj-wrap">
          <div className="gpj-sec-head">
            <span className="gpj-eyebrow">Forms library</span>
            <h2>Ontario family court forms</h2>
            <p>
              Forms open on the official Ontario Court Services website. GPJ does
              not store copies of official forms.
            </p>
          </div>

          {catalogue && (
            <div className="gpj-catalogue-status" role="status">
              <strong>Handoff starter set</strong>
              <span>
                The master Forms Hub inventory is not available yet. This is not
                the complete Ontario catalogue.
              </span>
            </div>
          )}

          <label className="gpj-searchbar">
            <span className="gpj-search-icon" aria-hidden="true">⌕</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by form number or by what you need to do"
              aria-label="Search forms"
            />
          </label>

          <div className="gpj-filters" aria-label="Filter forms by category">
            {filters.map((filter) => (
              <button
                type="button"
                key={filter}
                className={`gpj-filter${activeFilter === filter ? ' is-active' : ''}`}
                onClick={() => setActiveFilter(filter)}
                aria-pressed={activeFilter === filter}
              >
                {filter}
              </button>
            ))}
          </div>

          {loading && <p className="gpj-loading" role="status">Loading forms…</p>}
          {error && <p className="gpj-error" role="alert">{error}</p>}
          {actionError && <p className="gpj-error" role="alert">{actionError}</p>}
          {guideLoading && <p className="gpj-loading" role="status">Loading field guide…</p>}

          {accessRequired && (
            <div className="gpj-access-required" role="alert">
              <strong>Access required</strong>
              <p>
                Guided form help requires active full-platform access or an
                entitlement for this form. Browsing official forms remains public.
              </p>
              <button
                type="button"
                className="gpj-btn gpj-btn--primary gpj-btn--sm"
                onClick={() => setCurrentView('access-selection')}
              >
                View Access options
              </button>
            </div>
          )}

          {!loading && !error && (
            <div className="gpj-forms-grid">
              {visible.map((form) => (
                <FormCard
                  key={`${form.form_id}:${form.catalogue_version}`}
                  form={form}
                  user={user}
                  onGuide={showGuide}
                  onStart={startWorkflow}
                  onLogin={() => setCurrentView('login')}
                />
              ))}
              {visible.length === 0 && (
                <p className="gpj-empty" role="status">
                  No forms match this search and filter.
                </p>
              )}
            </div>
          )}

          {guide && (
            <FieldGuide
              guide={guide}
              user={user}
              onWritingReview={setCurrentView}
            />
          )}

          <p className="gpj-disclaimer">
            <b>Sourcing.</b> Form numbers, titles, status, and links are controlled
            by the server catalogue and point to Ontario Court Services. Always
            use the official source. GPJ guidance does not replace the form or
            legal advice.
          </p>
        </div>
      </section>
    </div>
  );
}
